<script setup lang="ts">
import { ref, computed } from "vue";
import { Option } from "@/types";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAutocompleteUsers } from "@/api/mutations";
import { UsersAutocompleteResult } from "@telegraphio/bapi-types";

type Props = {
  customerId: string;
  flat?: boolean;
  name: string;
  required?: boolean;
  value?: UsersAutocompleteResult;
};

const autocompleteUsersMutation = useAutocompleteUsers();

const props = defineProps<Props>();
const profilePictureURLs = ref<Map<string, string>>(new Map());
const initialsMap = ref<Map<string, string>>(new Map());
const optionList = ref<Option[]>([]);
const fullName = computed(() => (props.value ? `${props.value?.first_name} ${props.value?.last_name}` : ""));

async function searchUsers({ search }: { search: string }): Promise<Option[]> {
  if (!search || search.length < 3) {
    return optionList.value;
  }

  const responseData = await autocompleteUsersMutation.mutateAsync({
    customerId: props.customerId,
    requestData: {
      search_term: search,
      page: 1,
      page_size: 100,
    },
  });

  if (!responseData.data.length) {
    return optionList.value;
  }

  const out: Option[] = [];
  for (const i of responseData.data) {
    if (!optionList.value.find((opt) => opt.value === i.user_id)) {
      if (i.profile_picture) {
        profilePictureURLs.value.set(i.user_id, i.profile_picture.thumbnail_url);
      }
      initialsMap.value.set(i.user_id, `${i.first_name.charAt(0)}${i.last_name.charAt(0)}`);
      out.push({
        label: i.first_name + " " + i.last_name,
        value: i.user_id,
      });
    }
  }

  return optionList.value.concat(out);
}

async function optionLoader(value: string, cached?: Option): Promise<Option> {
  if (props.value) {
    if (props.value.profile_picture) {
      profilePictureURLs.value.set(props.value.user_id, props.value.profile_picture?.thumbnail_url);
    }
    initialsMap.value.set(props.value.user_id, `${props.value.first_name.charAt(0)}${props.value.last_name.charAt(0)}`);
    return {
      label: `${props.value.first_name} ${props.value.last_name}`,
      value: props.value.user_id,
    };
  }

  if (cached) {
    if (!optionList.value.find((opt) => opt.value === cached.value)) {
      optionList.value.push(cached);
    }
    return cached;
  }

  const responseData = await autocompleteUsersMutation.mutateAsync({
    customerId: props.customerId,
    requestData: {
      search_term: fullName.value || value,
      page: 1,
      page_size: 100,
    },
  });

  if (!responseData.data.length) {
    return { label: "No matching users found.", value: "" };
  }

  const out: Option[] = [];
  for (const i of responseData.data) {
    if (i.profile_picture) {
      profilePictureURLs.value.set(i.user_id, i.profile_picture.thumbnail_url);
    }
    initialsMap.value.set(i.user_id, `${i.first_name.charAt(0)}${i.last_name.charAt(0)}`);
    out.push({
      label: i.first_name + " " + i.last_name,
      value: i.user_id,
    });
  }

  optionList.value.concat(out);
  return out[0];
}
</script>

<template>
  <FormKit
    :name="name"
    type="autocomplete"
    :close-on-select="true"
    selection-appearance="option"
    loader-icon="loader"
    :delay="300"
    :validation="required ? 'required' : ''"
    :classes="{
      outer: '!mb-0',
      wrapper: '!mb-0',
      inner: `${flat ? 'rounded-none shadow-inner w-56' : 'w-56'}`,
      dropdownWrapper: 'rounded-none',
      listbox: 'rounded-none border-2',
      option: 'rounded-none',
      loadMoreInner: 'hidden',
      selectionWrapper: 'bg-white border-none rounded-none m-px justify-between',
    }"
    :options="searchUsers"
    :option-loader="optionLoader"
    :value="value?.user_id"
    empty-message="No users found."
    placeholder="Search Name"
  >
    <template #option="{ option }">
      <div class="flex h-8 w-full items-center gap-1.5 p-0">
        <Avatar class="h-8 w-8">
          <AvatarImage :src="profilePictureURLs.get(option.value) ?? ''" :alt="`${option.label} profile picture`" />
          <AvatarFallback>
            <span class="inline-flex h-full w-full items-center justify-center rounded-full bg-gray-500">
              <span class="text-xs leading-none font-medium text-white">
                {{ initialsMap.get(option.value) }}</span
              ></span
            >
          </AvatarFallback>
        </Avatar>
        <span class="font-medium">{{ option.label }}</span>
      </div>
    </template>
  </FormKit>
</template>
